import { getDefaultOrderBarcode } from 'common/modules/orderReceipts/utils';
import { getExternalPosTriggerTypes, getPosIntegrationType } from 'common/modules/posIntegrations';
import { getCloudPosOrderNumber } from 'common/modules/posIntegrations/cloudPos';
import { getTeksoOrderNumber } from 'common/modules/posIntegrations/tekso';
import { ShopApi, ShopReadOnlyData } from 'common/types';
import { fillWithLeadingZeros } from 'common/utils/common';

interface ReceiptBarCode {
	readOnlyData: ShopReadOnlyData | undefined;
	shopInfo: ShopApi;
	paymentNumber: number;
	amount: number;
	orderNumber: number | undefined;
	orderId: string;
	receiptType: 'shopper' | 'order' | null;
}

export const getReceiptBarCode = ({
	readOnlyData,
	shopInfo,
	paymentNumber,
	amount,
	orderNumber,
	orderId,
	receiptType,
}: ReceiptBarCode): string => {
	const posIntegrationType = getPosIntegrationType(readOnlyData);
	const externalPosTriggerTypes = getExternalPosTriggerTypes({
		readOnly: readOnlyData,
		shopInfo,
	});
	if (
		!posIntegrationType ||
		(receiptType === 'shopper' && !externalPosTriggerTypes.includes('onShopperReceiptPrint')) ||
		(receiptType === 'order' && !externalPosTriggerTypes.includes('onOrderReceiptPrint'))
	) {
		return getDefaultOrderBarcode(orderNumber, orderId);
	}
	switch (posIntegrationType) {
		case 'OSKAR': {
			const codePrefix = 'VT';
			let paymentNumberNineChars = fillWithLeadingZeros(paymentNumber.toString(), 9);
			let priceInCentsSixChars = fillWithLeadingZeros(Math.max(amount, 0).toString(), 6);
			return codePrefix + paymentNumberNineChars + priceInCentsSixChars;
		}
		case 'TEKSO': {
			const codePrefix = '1';
			const { customerId, orderBaseNumber } = readOnlyData!.integrations!.TEKSO!; // Must be defined if TEKSO value returned
			const customerIdNineChars = fillWithLeadingZeros(customerId, 9);
			const teksoOrderNumber = getTeksoOrderNumber(orderBaseNumber, paymentNumber);
			const receiptBarCode = codePrefix + customerIdNineChars + teksoOrderNumber;
			return receiptBarCode;
		}
		case 'CLOUD_POS': {
			const codePrefix = '_oRENTLE:';
			const orderBaseNumber = readOnlyData?.integrations?.CLOUD_POS?.orderBaseNumber;
			const cloudPosOrderNumber = getCloudPosOrderNumber(orderBaseNumber, paymentNumber);
			const receiptBarCode = codePrefix + cloudPosOrderNumber;
			return receiptBarCode;
		}
		default: {
			return getDefaultOrderBarcode(orderNumber, orderId);
		}
	}
};
